import { supabase } from '../supabaseClient.js';

const MASTERY_INTERVAL_THRESHOLD = 21; // Days

export const calculateRuleStatus = (rule, userProgress) => {
    // 1. If no progress data exists
    if (!userProgress) return 'not_started';

    const srsData = userProgress.srs_data || {};
    const hasStartedLearning = srsData.learningStepIndex !== undefined;
    const isGraduated = srsData.learningStepIndex === -1;
    const interval = srsData.currentInterval || 0;

    // 2. Determine SRS Status
    let srsStatus = 'not_started';
    if (hasStartedLearning && !isGraduated) srsStatus = 'learning';
    else if (isGraduated && interval < MASTERY_INTERVAL_THRESHOLD) srsStatus = 'reviewing';
    else if (isGraduated && interval >= MASTERY_INTERVAL_THRESHOLD) srsStatus = 'srs_mastered';

    // 3. Check Quiz Status for HFRs
    const isHFR = rule.isHFR; 
    const attemptHistory = userProgress.attempt_history || []; // Updated from legacy quiz_history
    const hasPassedQuiz = attemptHistory.some(a => a.source === 'quiz' && a.userScore >= 2);

    // 4. Final Status Logic
    if (srsStatus === 'not_started') return 'not_started';
    if (srsStatus === 'learning') return 'in_progress';
    if (srsStatus === 'reviewing') return 'in_progress';

    if (srsStatus === 'srs_mastered') {
        // If it's a High Frequency Rule, we enforce the Quiz requirement
        if (isHFR && !hasPassedQuiz) {
            return 'in_progress'; 
        }
        return 'mastered';
    }

    return 'not_started'; 
};

/**
 * ASYNC: Calculates refund eligibility by querying activity logs.
 * Now returns a Promise that resolves to the eligibility object.
 */
export const checkRefundEligibility = async (userId, userData, allItemsMap, thresholdPercent = 10) => {
    if (!userId || !userData || !allItemsMap) return { eligible: false, percent: 0, count: 0, total: 0 };

    // 1. Get Total Rule Count (Canonical)
    let totalRules = 0;
    allItemsMap.forEach(item => {
        if (item.itemType === 'rule') totalRules++;
    });

    if (totalRules === 0) return { eligible: true, percent: 0, count: 0, total: 0 };

    // 2. Identify Unique Consumed IDs
    const consumedIds = new Set();

    // A. Rules learned (from local State - fastest)
    if (userData.rulesProgress) {
        Object.keys(userData.rulesProgress).forEach(id => {
            // If it has SRS data, they've 'seen' it in Learn Mode
            if (userData.rulesProgress[id]?.srs_data) {
                consumedIds.add(id);
            }
        });
    }

    // B. Rules passively viewed (Fetch from DB Logs)
    // We fetch unique rule IDs where the user performed a 'view_rule' action
    try {
        // We use a Remote Procedure Call (RPC) or a distinct select if possible.
        // For simplicity and robustness without new SQL functions, we fetch the IDs.
        // In a high-scale app, we would make a specific RPC for "count_unique_views".
        const { data, error } = await supabase
            .from('user_activity_log')
            .select('entity_id')
            .eq('user_id', userId)
            .eq('action_type', 'view_rule');

        if (!error && data) {
            data.forEach(row => {
                if (row.entity_id) consumedIds.add(row.entity_id);
            });
        }
    } catch (e) {
        console.error("Error checking refund eligibility:", e);
    }

    // 3. Calculate Stats
    const consumedCount = consumedIds.size;
    const percentSeen = (consumedCount / totalRules) * 100;

    return {
        eligible: percentSeen < thresholdPercent,
        percent: parseFloat(percentSeen.toFixed(1)),
        count: consumedCount,
        total: totalRules
    };
};

export const calculateLocalStreak = (docket) => {
    if (!docket?.schedule || !Array.isArray(docket.schedule)) return 0;
    
    const historyMap = new Map();
    docket.schedule.forEach(day => historyMap.set(day.date, day));

    let streak = 0;
    const today = new Date();
    
    const getStr = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const isDayActive = (entry) => {
        if (!entry || !entry.metrics) return false;
        const m = entry.metrics;
        return (m.learned_count > 0 || m.quiz_count > 0 || (m.review_count || 0) > 0);
    };

    const todayEntry = historyMap.get(getStr(today));
    if (isDayActive(todayEntry)) {
        streak++;
    }

    let checkDate = new Date(today);
    
    for (let i = 0; i < 3650; i++) {
        checkDate.setDate(checkDate.getDate() - 1);
        const dateStr = getStr(checkDate);
        const entry = historyMap.get(dateStr);

        if (isDayActive(entry)) {
            streak++;
        } else {
            const isBreak = entry?.is_break_day === true;
            if (!isBreak) {
                break; 
            }
        }
    }
    
    return streak;
};
